import { ButtonBase } from "@components/Button";
import useClientContext, { setModal } from "@hooks/useClientContext";
import { DialogContent, DialogOverlay, DialogOverlayProps } from "@reach/dialog";
import "@reach/dialog/styles.css";
import { PropsWithChildren } from "react";
import { hexRGBA, media, pxToRem } from "src/styles/styleUtils";
import styled, { css } from "styled-components";
import CloseSVG from "./svgs/x-icon.svg";

type ModalProps = PropsWithChildren<
  {
    hideCloseIcon?: boolean;
  } & DialogOverlayProps
>;

function Modal({ hideCloseIcon = false, children, ...otherProps }: ModalProps) {
  const [, dispatch] = useClientContext();
  const closeModal = () => dispatch(setModal(null));

  return (
    <Overlay aria-label="Dialog Overlay" onDismiss={closeModal} {...otherProps}>
      <DialogContent aria-label="Dialog Content">
        {!hideCloseIcon && (
          <CloseBtn onClick={otherProps.onDismiss || closeModal} className="closeIcon">
            <CloseSVG />
          </CloseBtn>
        )}
        {children}
      </DialogContent>
    </Overlay>
  );
}

export default Modal;

const Overlay = styled(DialogOverlay)<ModalProps>`
  [data-reach-dialog-content] {
    position: relative;
    padding: 0;
    width: 100%;
    max-width: ${pxToRem(760)};

    ${media.mobile} {
      max-width: 90vw;
    }
  }
`;

const CloseBtn = styled.button`
  ${ButtonBase}
  position: absolute;
  top: ${pxToRem(10)};
  right: ${pxToRem(10)};
  padding: ${pxToRem(8)};
  color: white;

  &:focus {
    position: absolute; /* redundant because NascarNav styles inject a position: relative on button focus which collapses the UI */
  }
  svg {
    width: ${pxToRem(20)};
  }
`;

//* Optional importable Modal Components ↓

/* NascarLive modal theme */

export const ModalHeader = styled.h2`
  margin: 0;
  padding: ${pxToRem(16)} ${pxToRem(24)};
  background: black;
  color: white;
  text-transform: uppercase;
`;

export const ModalSubHeader = styled.p`
  margin: 0;
  padding: ${pxToRem(12)} ${pxToRem(24)};
  background: ${({ theme }) => theme.grey_500};
  color: white;
`;

export const ModalContent = styled.div`
  padding: ${pxToRem(16)} ${pxToRem(24)} ${pxToRem(24)};
  ${media.mobile} {
    padding: ${pxToRem(15)};
  }
`;

/* ↓ Playoffs styled Modal elements ↓ */

/**
 * Extensible Playoffs modal theme.
 */
export const playoffsModalCSS = css`
  &[data-reach-dialog-overlay] {
    background: ${hexRGBA("#42474B", 0.6)};
    display: flex;
    justify-content: center;
    z-index: 999;
  }

  [data-reach-dialog-content] {
    margin: clamp(${pxToRem(96)}, 20vh, ${pxToRem(260)}) auto auto;
    max-width: ${pxToRem(520)};
    box-shadow: 0px 0px ${pxToRem(20)} ${pxToRem(10)} ${({ theme }) => hexRGBA(theme.grey_700, 0.6)};
    background-color: ${({ theme }) => theme.grey_700};
    color: white;
  }
`;

export const ChaseModalWrapper = styled.div`
  padding: ${pxToRem(35)} ${pxToRem(25)} ${pxToRem(28)};
`;

export const ChaseContentContainer = styled.div`
  border: ${({ theme }) => `${pxToRem(2)} solid ${theme.gold}`};
`;

export const ChaseHeaderContent = styled.div`
  text-align: center;
  text-transform: uppercase;

  h2 {
    display: inline-block;
    padding: 0 ${pxToRem(14)};
    margin: 0 0 ${pxToRem(8)};
    font-size: ${pxToRem(36)};
    line-height: 1;
    position: relative;
    top: ${pxToRem(-18)};
    background-color: ${({ theme }) => theme.grey_700};
    color: ${({ theme }) => theme.gold};
    font-family: ${({ theme }) => theme.fontSecondary};
    font-weight: normal;
  }
  p {
    margin: 0 auto ${pxToRem(22)};
    padding: 0 ${pxToRem(10)};
    max-width: ${pxToRem(380)};
    font-size: ${pxToRem(12)};
    line-height: 1.33;

    span {
      font-style: italic;
    }
  }
`;
